/**
 * BudgetSiteEditDialog — Budget page shortcut to open the individual site
 * edit dialog from Reports > Budget beside each site name.
 *
 * Routing rules (match Customers page):
 *  • Grouped site (is_independent = false):
 *      Opens SharedSiteEditDialog — the exact same full editor used by
 *      CustomersPage, with all field sections, contacts, pricing, logo,
 *      email script, budget history, and detach-from-group.
 *  • Independent site (is_independent = true):
 *      Open IndependentCustomerDialog, loading the client via clientsService.get.
 *
 * Props:
 *  siteId        (number)    — site to edit
 *  clientId      (number)    — parent_client_id (grouped) or independent_client_id
 *  isIndependent (bool)      — routing switch
 *  siteName      (string)    — used in trigger tooltip / dialog loading state
 *  clientName    (string)    — parent customer name for email preview sample data
 *  onSaved       (fn)        — called with updated site data after a successful save
 *  onDetached    (fn)        — called after a successful detach-from-group
 *  trigger       (ReactNode) — custom trigger element (e.g. an icon button)
 *
 * Budget contract:
 *  • Does NOT re-fetch or mutate the budget grid state itself.
 *  • Calls onSaved(updatedSite) so the caller can refresh only the relevant row.
 *  • Opening the dialog leaves Budget filters/scroll/unsaved edits untouched.
 */
import React, { useState, useCallback } from 'react';
import { Pencil } from 'lucide-react';
import { clientsService } from '../services';
import IndependentCustomerDialog from './IndependentCustomerDialog';
import SharedSiteEditDialog from './SharedSiteEditDialog';
import { toast } from './ui/sonner';

// ── Main exported component ───────────────────────────────────────────────────
export function BudgetSiteEditDialog({
  siteId, clientId, isIndependent, siteName, clientName,
  onSaved, onDetached, trigger,
}) {
  const [open, setOpen] = useState(false);
  // Load client to determine actual client_kind (budget is_independent flag may
  // disagree with client_kind when site uses parent_client_id for an independent client).
  const [independentCustomer, setIndependentCustomer] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);

  const handleOpen = useCallback(async () => {
    setLoadingClient(true);
    try {
      const client = await clientsService.get(clientId);
      if (client.client_kind?.toLowerCase() === 'independent') {
        setIndependentCustomer(client);
      }
      setOpen(true);
    } catch (err) {
      toast.error('Failed to load customer data');
    } finally {
      setLoadingClient(false);
    }
  }, [clientId]);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setIndependentCustomer(null);
  };

  return (
    <>
      <span
        onClick={(e) => { e.stopPropagation(); handleOpen(); }}
        className="inline-flex"
      >
        {trigger || (
          <button
            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring"
            title={`Edit site: ${siteName}`}
            data-testid={`budget-edit-site-btn-${siteId}`}
            disabled={loadingClient}
          >
            <Pencil size={11} className="text-muted-foreground" />
          </button>
        )}
      </span>

      {independentCustomer !== null ? (
        <IndependentCustomerDialog
          open={open}
          onOpenChange={handleOpenChange}
          customer={independentCustomer}
          onSaved={(updated) => { onSaved?.(updated); }}
        />
      ) : (
        <SharedSiteEditDialog
          open={open}
          onOpenChange={handleOpenChange}
          siteId={siteId}
          siteName={siteName}
          customerName={clientName}
          onSaved={(updated) => { onSaved?.(updated); }}
          onDetached={() => { onDetached?.(); }}
        />
      )}
    </>
  );
}

export default BudgetSiteEditDialog;
